import React, { createContext, useContext, useEffect, useMemo } from 'react';
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Rate,
  Row,
  Space,
  Typography,
} from 'antd';

import {
  createEmptyDraft,
  FEEDBACK_FIELDS,
  RATE_FIELD_RULES,
  RATING_ROWS,
} from './constants';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { toastSuccess } from '../Toast/ToastComponent';

const { Title, Text, Paragraph } = Typography;

/** Display split for the read-only summary (left | right) - uses rating IDs. */
const SUMMARY_RATING_COLUMNS = [
  [3, 4, 6], // WORK QUALITY, COMMUNICATION, DEADLINE ADHERENCE
  [1, 5], // PRODUCTIVITY, TEAM COLLABORATION
];

/** Display labels used on the read-only summary card. */
const SUMMARY_FEEDBACK_BLOCKS = [
  {
    key: 'achievements',
    label: 'Key Achievements',
    value:
      'Successfully led the migration of our authentication system to OAuth 2.0, improving security and user experience. Mentored 2 junior developers and helped them ship their first features.',
  },
  {
    key: 'challenges',
    label: 'Challenges Faced',
    value:
      'Successfully led the migration of our authentication system to OAuth 2.0, improving security and user experience. Mentored 2 junior developers and helped them ship their first features.',
  },
  {
    key: 'suggestions',
    label: 'Suggestions',
    value:
      'Successfully led the migration of our authentication system to OAuth 2.0, improving security and user experience. Mentored 2 junior developers and helped them ship their first features.',
  },
];

const labelForRateKey = (key) => {
  const row = RATING_ROWS.find((r) => (r.key ?? r.id) === key);
  return row?.label ?? row?.skill_name ?? key;
};

const capitalizedTitle = (val) => {
  if (!val || typeof val !== 'string') return '--';
  return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
};

const SelfReviewMonthFormContext = createContext(null);

function useSelfReviewMonthForm() {
  const ctx = useContext(SelfReviewMonthFormContext);
  if (ctx == null) {
    throw new Error(
      'SelfReviewMonthForm compound components must be used inside SelfReviewMonthForm.',
    );
  }
  return ctx;
}

function mergeDraftFromFormValues(all) {
  const empty = createEmptyDraft();
  const employeeReview = all?.employee_review || {};
  return {
    rates: { ...empty.rates, ...(employeeReview?.rates || {}) },
    feedback: { ...empty.feedback, ...(employeeReview?.feedback || {}) },
  };
}

function ReviewFormRoot({
  monthLabel,
  readOnly,
  value,
  onChange,
  children,
  className,
  onSubmit,
  onSaveDraft,
  onRevertToManager,
}) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!value) return;
    form.setFieldsValue({
      ...value,
    });
  }, [value]);

  const handleValuesChange = () => {
    const all = form.getFieldsValue(true);
    onChange && onChange(mergeDraftFromFormValues(all));
  };

  const handleFinish = (values) => {
    if (onSubmit) {
      onSubmit(values);
    } else {
      toastSuccess('Review submitted. Connect API to persist.');
    }
  };

  const handleRevertToManager = () => {
    const values = form.getFieldsValue(true);
    if (onRevertToManager) {
      onRevertToManager(values);
    } else {
      toastSuccess('Review reverted to manager. Connect API to persist.');
    }
  };

  const handleSaveDraft = () => {
    const values = form.getFieldsValue(true);
    if (onSaveDraft) {
      onSaveDraft(values);
    } else {
      handleValuesChange();
      toastSuccess('Draft saved locally. Connect API to persist.');
    }
  };

  return (
    <SelfReviewMonthFormContext.Provider
      value={{
        form,
        readOnly,
        saveDraft: handleSaveDraft,
        revertToManager: handleRevertToManager,
      }}
    >
      <div
        className={`${className ? `${className} ` : ''} border-top`}
        aria-label={`Self review form for ${monthLabel}`}
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={readOnly}
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
          onFinish={handleFinish}
          onValuesChange={handleValuesChange}
        >
          {children}
        </Form>
      </div>
    </SelfReviewMonthFormContext.Provider>
  );
}

function TimesheetSummary({
  summary = {},
  title = 'Timesheet Summary',
  className,
}) {
  const {
    totalHours = 0,
    projects = 0,
    leaves = 0,
    attendancePct = 0,
  } = summary;

  return (
    <section
      className={`${className ? className : ''} bg-white p-3`}
      aria-label={title}
    >
      <Title level={4} className="text-dark fw-semibold">
        {title}
      </Title>
      {/* Hours, Projects, Leaves, Attendance */}
      <Flex gap={16} wrap="wrap">
        <Flex vertical className="flex-fill">
          <Text type="secondary" className="mb-1 fw-medium">
            Total Hours Logged
          </Text>

          <Text className="fs-3 fw-bold primaryColor">{totalHours}</Text>
        </Flex>

        <Flex vertical className="flex-fill">
          <Text type="secondary" className="mb-1 fw-medium">
            Projects Worked
          </Text>
          <Text className="fs-3 fw-bold primaryColor">{projects}</Text>
        </Flex>

        <Flex vertical className="flex-fill">
          <Text type="secondary" className="mb-1 fw-medium">
            Leaves
          </Text>
          <Text className="fs-3 fw-bold primaryColor">{leaves}</Text>
        </Flex>

        <Flex vertical className="flex-fill">
          <Text type="secondary" className="mb-1 fw-medium">
            Attendance
          </Text>
          <Text type="success" className="fs-3 fw-bold">
            {attendancePct}%
          </Text>
        </Flex>
      </Flex>
    </section>
  );
}

function PerformanceRating({
  title,
  subtitle = 'Rate yourself on a scale of 1-5 stars',
  rows = [],
  parentKey = [],
  className,
}) {
  return (
    <section
      className={`${className ? `${className} ` : ''}bg-white p-4 rounded-1`}
      aria-label={title}
    >
      <Title level={4} className="text-dark fw-semibold mb-1">
        {title}
      </Title>
      <Text type="secondary" className="d-block mb-3 fw-medium">
        {subtitle}
      </Text>
      {rows?.map((row, index) => {
        const rowKey = row?.id ?? index;
        const rowLabel = row?.skill_name;
        return (
          <Row
            key={rowKey}
            className={`py-2 ${index === rows.length - 1 ? 'border-bottom-0' : 'border-bottom'}`}
            justify={'space-between'}
            align={'middle'}
          >
            <Col xs={12} sm={6} className="mb-2 mb-sm-0">
              <Text className="mb-1 fw-medium">
                {capitalizedTitle(rowLabel)}
              </Text>
            </Col>
            <Col xs={12} sm={6} className="text-sm-end">
              <Form.Item
                name={[...parentKey, index, 'value']}
                rules={RATE_FIELD_RULES}
                style={{ marginBottom: 0 }}
                validateTrigger={['onChange', 'onBlur']}
              >
                <Rate count={5} />
              </Form.Item>
            </Col>
          </Row>
        );
      })}
    </section>
  );
}

function Feedback({ title, subtitle, fields, className, parentKey = [] }) {
  return (
    <section
      className={`${className ? `${className} ` : ''}bg-white p-4 rounded-1`}
      aria-label={title}
    >
      <Title level={4} className="text-dark fw-semibold mb-1">
        {title}
      </Title>
      {subtitle ? (
        <Text type="secondary" className="d-block mb-3 fw-medium">
          {subtitle}
        </Text>
      ) : null}
      {fields.map(({ key, label, placeholder }, index) => (
        <Form.Item
          key={index}
          label={<Text className="fw-medium text-dark">{label}</Text>}
          name={[...parentKey, index, 'answer']}
        >
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 12 }}
            placeholder={placeholder}
            className="rounded"
          />
        </Form.Item>
      ))}
    </section>
  );
}

function Actions({ className }) {
  const { readOnly, saveDraft } = useSelfReviewMonthForm();
  return (
    <Flex
      justify="flex-end"
      align="center"
      wrap="wrap"
      gap="middle"
      className={`${className ? `${className} ` : ''}bg-white p-4 rounded-1`}
    >
      <Button icon={<SaveOutlined />} onClick={saveDraft} disabled={readOnly}>
        Save Draft
      </Button>
      <Button
        type="primary"
        htmlType="submit"
        icon={<SendOutlined />}
        disabled={readOnly}
      >
        Submit Review
      </Button>
    </Flex>
  );
}

function OverallRating({ title, subtitle, name, className }) {
  return (
    <section
      className={`${className ? `${className} ` : ''}bg-white p-4 rounded-1`}
      aria-label={title}
    >
      <Title level={5} className="text-dark fw-semibold mt-0 mb-1">
        {title}
      </Title>
      <Text type="secondary" className="d-block mb-3 fw-medium">
        {subtitle}
      </Text>
      <Flex justify="center">
        <Form.Item name={name} style={{ marginBottom: 0 }}>
          <Rate count={5} style={{ fontSize: 32 }} />
        </Form.Item>
      </Flex>
    </section>
  );
}

function EmployeeReviewSummary({
  headerTitle = 'Employee Self Review Summary',
  summary = {},
  rates = [],
  feedback = [],
  className,
}) {
  const summaryRates = useMemo(() => {
    const leftRates = rates.slice(0, Math.ceil(rates.length / 2));
    const rightRates = rates.slice(Math.ceil(rates.length / 2));
    return [leftRates, rightRates];
  }, [rates]);
  return (
    <div
      className={`p-4 rounded-1 ${className || ''}`.trim()}
      style={{ background: '#f1f8fd', border: '1px solid #bae6fd' }}
    >
      <Space direction="vertical" size="middle" className="w-100">
        <Title level={4} className="text-dark fw-semibold mt-0 mb-0">
          {headerTitle}
        </Title>
        <TimesheetSummary summary={summary} />

        {/* Ratings View */}
        <section
          className={`${className ? `${className} ` : ''} bg-white p-4 rounded-1`}
          aria-label="Employee Ratings"
        >
          <Title level={5} className="text-dark fw-semibold mt-0 mb-3">
            Employee Ratings
          </Title>
          <Row gutter={[24, 24]}>
            {summaryRates.map((ratesCol, colIndex) => (
              <Col xs={24} md={12} key={colIndex}>
                <Flex vertical gap={10}>
                  {ratesCol.map((rate, idx) => (
                    <Flex
                      key={rate?.id ?? idx}
                      justify="space-between"
                      align="center"
                      wrap="wrap"
                      gap={8}
                    >
                      <Text type="secondary" className="fw-medium">
                        {capitalizedTitle(rate?.skill_name)}
                      </Text>
                      <Rate
                        disabled
                        allowHalf={false}
                        value={rate?.value ?? 0}
                      />
                    </Flex>
                  ))}
                </Flex>
              </Col>
            ))}
          </Row>
        </section>

        {/* Feedback View */}

        <section
          className={`${className ? `${className} ` : ''} bg-white p-4 rounded-1`}
          aria-label="Employee Feedback"
        >
          {feedback.map((val, idx) => {
            const question = typeof val === 'object' ? val?.question : val;
            const answer = typeof val === 'object' ? val?.answer : '';
            return (
              <div key={idx} className={''}>
                <Title level={5} className="text-dark fw-semibold mb-1">
                  {capitalizedTitle(question)}
                </Title>
                <Paragraph type="secondary" className="fw-medium">
                  {answer || '—'}
                </Paragraph>
              </div>
            );
          })}
        </section>
      </Space>
    </div>
  );
}

function ManagerReviewSummary({
  headerTitle = 'Manager Review Summary',
  overallRating = 0,
  rates = [],
  managerFeedback = [],
  className,
}) {
  const summaryRates = useMemo(() => {
    const leftRates = rates.slice(0, Math.ceil(rates.length / 2));
    const rightRates = rates.slice(Math.ceil(rates.length / 2));
    return [leftRates, rightRates];
  }, [rates]);

  return (
    <div
      className={`p-4 rounded-1 ${className || ''}`.trim()}
      style={{ background: '#fefce8', border: '1px solid #fef08a' }}
    >
      <Space direction="vertical" size="middle" className="w-100">
        <Title level={4} className="text-dark fw-semibold mt-0 mb-0">
          {headerTitle}
        </Title>

        {/* Overall Rating + Category Ratings */}
        <Row gutter={[16, 16]} align="stretch">
          {/* Left: Overall Rating */}
          <Col xs={24} md={8} className="d-flex">
            <section className="bg-white p-4 rounded-1 w-100 d-flex flex-column">
              <Text className="d-block fw-semibold text-dark mb-2">
                Overall Rating
              </Text>
              <Flex align="center" gap={8}>
                <Rate disabled allowHalf value={overallRating} />
                <Text className="fw-bold text-dark fs-5">
                  {overallRating}/5
                </Text>
              </Flex>
            </section>
          </Col>
          {/* Right: Category Ratings */}
          <Col xs={24} md={16} className="d-flex">
            <section className="bg-white p-4 rounded-1 w-100">
              <Title level={5} className="text-dark fw-semibold">
                Category Ratings
              </Title>
              <Row gutter={[8, 8]}>
                {summaryRates.map((ratesCol, colIndex) => (
                  <Col
                    key={colIndex}
                    xs={24}
                    md={rates.length > 6 ? 12 : 24}
                    className="gutter-row d-flex flex-column gap-2"
                  >
                    {ratesCol.map((rate, idx) => (
                      <Flex
                        key={rate?.id ?? idx}
                        justify="space-between"
                        align="center"
                        wrap="wrap"
                        gap={8}
                      >
                        <Text type="secondary" className="fw-medium">
                          {capitalizedTitle(rate?.skill_name)}
                        </Text>
                        <Rate
                          disabled
                          allowHalf={false}
                          value={rate?.value ?? 0}
                        />
                      </Flex>
                    ))}
                  </Col>
                ))}
              </Row>
            </section>
          </Col>
        </Row>

        {/* Manager Feedback */}
        <section className="bg-white p-4 rounded-1">
          {Array.isArray(managerFeedback) && managerFeedback.length > 0 ? (
            managerFeedback.map((item, idx) => (
              <div
                key={idx}
                className={idx < managerFeedback.length - 1 ? 'mb-4' : ''}
              >
                <Title level={5} className="text-dark fw-semibold mb-1">
                  {capitalizedTitle(item?.question)}
                </Title>
                <Paragraph type="secondary" className="fw-medium mb-0">
                  {item?.answer || '—'}
                </Paragraph>
              </div>
            ))
          ) : (
            <Paragraph type="secondary" className="mb-0">
              No feedback provided.
            </Paragraph>
          )}
        </section>
      </Space>
    </div>
  );
}

function HRReview({
  title,
  commentsLabel,
  commentsPlaceholder,
  commentsName,
  className,
}) {
  const { readOnly, revertToManager } = useSelfReviewMonthForm();
  return (
    <section
      className={`${className ? `${className} ` : ''}bg-white p-4 rounded-1`}
      aria-label={title}
    >
      <Title level={4} className="text-dark fw-semibold mt-0 mb-3">
        {title}
      </Title>

      {/* HR Comments */}
      <div className="mb-4">
        <Text className="d-block fw-medium text-dark mb-2">
          {commentsLabel}
        </Text>
        <Form.Item name={commentsName} style={{ marginBottom: 0 }}>
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 10 }}
            placeholder={commentsPlaceholder}
            className="rounded"
          />
        </Form.Item>
      </div>

      {/* Decision */}
      <div>
        <Text className="d-block fw-medium text-dark mb-3">Decision</Text>
        <Flex wrap="wrap" gap="middle" justify="end">
          <Button
            disabled={readOnly}
            icon={<CloseCircleOutlined />}
            onClick={revertToManager}
          >
            Send Back to Manager
          </Button>
          <Button
            className="bg-success text-white"
            icon={<CheckCircleOutlined />}
            disabled={readOnly}
            htmlType="submit"
          >
            Approve Review
          </Button>
        </Flex>
      </div>
    </section>
  );
}

function EmployeeInfoHeader({
  employeeName = '--',
  department = '--',
  manager = '--',
  reviewMonth = '--',
}) {
  const items = [
    { label: 'Employee Name', value: employeeName },
    { label: 'Department', value: department },
    { label: 'Manager', value: manager },
    { label: 'Review Month', value: reviewMonth },
  ];

  return (
    <div className="bg-white p-4 rounded-2">
      <Row gutter={[24, 12]}>
        {items.map((item) => (
          <Col key={item.label} xs={12} sm={6}>
            <Text type="secondary" className="d-block mb-1 fw-medium">
              {item.label}
            </Text>
            <Text strong style={{ fontSize: 14 }}>
              {item.value}
            </Text>
          </Col>
        ))}
      </Row>
    </div>
  );
}

function CompletedReviewSummary({
  headerTitle = '',
  hrComments = '',
  className,
}) {
  return (
    <div
      className={`p-4 rounded-1 ${className || ''}`.trim()}
      style={{ background: '#f1f8fd', border: '1px solid #bae6fd' }}
    >
      <Space direction="vertical" size="middle" className="w-100">
        <Title level={4} className="text-dark fw-semibold mt-0 mb-0">
          {headerTitle}
        </Title>
        <Text type="secondary" className="d-block mb-3 fw-medium">
          {hrComments || '—'}
        </Text>
      </Space>
    </div>
  );
}

const ReviewForm = Object.assign(ReviewFormRoot, {
  TimesheetSummary,
  PerformanceRating,
  Feedback,
  Actions,
  OverallRating,
  EmployeeReviewSummary,
  ManagerReviewSummary,
  HRReview,
  EmployeeInfoHeader,
  CompletedReviewSummary,
});

ReviewForm.displayName = 'ReviewForm';
export default ReviewForm;
